import React from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';
import { useUserStore } from '../store/userStore';
import { User } from '../features/chat/types';

export interface TeamRole {
  role_name: string;
}

export interface UserTeamAffiliation {
  team_id: number;
  team_name: string;
  roles: TeamRole[];
}

export interface UserLeagueAffiliation {
  league_id: number;
  league_name: string;
  league_year: number;
  team_id: number;
  team_name: string;
  teams: UserTeamAffiliation[];
}

export interface UserSettings {
  general_email: boolean;
  general_sms: boolean;
  team_management_email: boolean;
  team_management_sms: boolean;
  trade_email: boolean;
  trade_sms: boolean;
  waiver_fa_email: boolean;
  waiver_fa_sms: boolean;
  two_fa_enabled: boolean;
  user_id: number;
  sms_consent: boolean;
}

const fetchUserAffiliations = async (userId: number | undefined): Promise<UserLeagueAffiliation[]> => {
  console.log('fetchUserAffiliations called with userId:', userId);
  if (!userId) {
    console.log('No userId provided, returning empty array');
    return [];
  }
  try {
    const { data } = await apiClient.get('/user/get-user-roles');
    console.log('fetchUserAffiliations data:', data)
    return data;
  } catch (error) {
    console.error('Error fetching user affiliations:', error);
    return [];
  }
};

export const fetchUserSettings = async (userId: number | undefined): Promise<UserSettings> => {
  if (!userId) {
    console.log('No userId provided, returning default settings');
    return {
      general_email: false,
      general_sms: false,
      team_management_email: false,
      team_management_sms: false,
      trade_email: false,
      trade_sms: false,
      waiver_fa_email: false,
      waiver_fa_sms: false,
      user_id: 0
    };
  }
  try {
    const { data } = await apiClient.get(`/user/${userId}/settings`);
    return data;
  } catch (error) {
    console.error('Error fetching user settings:', error);
    return {
      general_email: false,
      general_sms: false,
      team_management_email: false,
      team_management_sms: false,
      trade_email: false,
      trade_sms: false,
      waiver_fa_email: false,
      waiver_fa_sms: false,
      user_id: 0
    };
  }
};


export const useUser = () => {
  const { user, setMyTeam } = useUserStore();

  const { data, isLoading, refetch, error } = useQuery({
    queryKey: ['userRoles', user?.id],
    queryFn: () => fetchUserAffiliations(user?.id),
    enabled: !!user, // Only run the query if the user is loaded
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    staleTime: 1000 * 60 * 5,    // Data is fresh for 5 minutes
  });

  // If we have league affiliations data and the user doesn't have a myTeam field in user_data,
  // populate it with the team ID from the first league
  React.useEffect(() => {

    if (data && data.length > 0 && user && !user.user_data?.myTeam) {
      console.log('data:', data);
      const firstLeague = data[0];
      
      if (firstLeague && firstLeague.team_id) {
        const teamId = Number(firstLeague.team_id);
        // setMyTeam is now async, so we need to handle it
        setMyTeam(teamId).catch((error: unknown) => {
          console.error('Failed to set myTeam:', error);
        });
      }
    } 
  }, [data, user, setMyTeam]);

  return {
    leagueAffiliations: data ?? [], 
    isLoading,
    error,
    refetch,
  };
};
 


// Hook to get a user's name by ID
export const useUserNameById = (userId: number | undefined) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['userName', userId],
    queryFn: async () => {
      if (!userId) return null;
      try {
        const { data } = await apiClient.get(`/user/${userId}/name`);

        return data.name || null;
      } catch (error) {
        console.error('Error fetching user name:', error);
        return null;
      }
    },
    enabled: !!userId, // Only run the query if userId is provided
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 10, // Data is fresh for 10 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });

  return {
    userName: data,
    isLoading,
    error,
  };
}; 

export const isAdmin = () => {
  const user = useUserStore(state => state.user);
  if (!user || !user.role) return false;
  return user.role?.toUpperCase() === 'ADMIN' || user.role?.toUpperCase() === 'SUPPORT';
};